/**
 * rowMode.ts
 *
 * Pure Row → Mode mapping and locked-Mode rules for the Workspace. No React,
 * no I/O: everything takes plain job state and returns plain data.
 *
 * - `getRowMode` maps a job's stage to the Mode its Workspace opens in
 *   (design.md, "Row → Mode"). It is total: every job maps to exactly one Mode
 *   that is valid for the job's Feature, with a sensible default for unknown
 *   statuses.
 * - `getLockedModes` returns the Modes that are locked, each with the reason
 *   shown in the Mode_Switch segment's `title`. The result is a `LockedModes`
 *   value, so it can be passed straight to `ModeSwitch`.
 * - `readJobWorkspaceParams` reads the URL with the Row → Mode default as the
 *   fallback for a missing or invalid `mode`, and never returns a locked Mode.
 *
 * Requirements: 2.6 (Row → Mode), 3.5 (draft items lock Modes),
 * 8.10 (TR Review_Gate).
 */

import { JobType } from '../graphql/types';
import { getStepStatus } from '../titleSearch/steps';
import type { LockedModes } from './modes';
import {
  WORKSPACE_MODES,
  readWorkspaceParams,
  type WorkspaceMode,
  type WorkspaceParams,
} from './urlState';

/**
 * The slice of a `Job` that Mode decisions depend on. `Job` satisfies this
 * structurally; statuses are accepted as raw strings so unknown or differently
 * cased values still map to a Mode instead of throwing.
 */
export interface RowModeJob {
  jobType?: JobType | string | null;
  /** Overall status (`JobStatus`). */
  status?: string | null;
  /** DD/BA step statuses (`StepStatus`); TR also uses `documentProcessingStatus`. */
  documentProcessingStatus?: string | null;
  documentVerificationStatus?: string | null;
  investorGuidelineReviewStatus?: string | null;
  /** TR: pending | in_progress | completed | failed | blocked. */
  titleAnalysisStatus?: string | null;
}

/** Options shared by the lock and default-Mode helpers. */
export interface ModeRuleOptions {
  /** True for an item that has not been created yet (the "New …" draft). */
  isDraft?: boolean;
}

/** Reason shown for the Modes a TR search cannot open before finalize. */
export const REVIEW_GATE_REASON = 'Finalize the review to unlock';

/** Unit of work per Feature, used in the draft lock reason. */
const UNIT_OF_WORK: Readonly<Record<JobType, string>> = {
  [JobType.DD]: 'job',
  [JobType.BA]: 'loan',
  [JobType.TR]: 'search',
};

function normalize(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

/** True when a step status means the step has begun (anything but not started). */
function hasStarted(status: string | null | undefined): boolean {
  const n = normalize(status);
  return n !== '' && n !== 'not_started' && n !== 'pending';
}

/** Feature of a job; anything that is not TR or BA is treated as DD. */
function featureOf(job: RowModeJob | null | undefined): JobType {
  const type = job?.jobType;
  if (type === JobType.TR || type === JobType.BA) return type;
  return JobType.DD;
}

/**
 * TR review is finalized once Document Processing has completed (finalizing
 * the review completes that step and advances to Findings Display). This is
 * the same signal `TitleSearchJobPage` uses for `docProcessingDone`.
 */
export function isTitleReviewFinalized(job: RowModeJob | null | undefined): boolean {
  if (!job) return false;
  return getStepStatus(job, 'document_processing') === 'completed';
}

/**
 * TR: every document has been processed and is waiting for the reviewer.
 * Post-BDA processing records the processed and failed loan counts and leaves
 * Document Processing in progress until the review is finalized, so this is
 * true from the end of extraction until finalize. Until then Review mode shows
 * the processing state (Requirement 6.7).
 */
export function isTitleReviewReady(
  job: (RowModeJob & { loanCount?: number | null; processedLoanCount?: number | null; failedLoanCount?: number | null }) | null | undefined
): boolean {
  if (!job || isTitleReviewFinalized(job)) return false;
  if (getStepStatus(job, 'document_processing') !== 'in_progress') return false;
  const total = job.loanCount ?? 0;
  const done = (job.processedLoanCount ?? 0) + (job.failedLoanCount ?? 0);
  return total > 0 && done >= total;
}

/** DD/BA: furthest stage reached according to the step statuses. */
function ddBaModeFromSteps(job: RowModeJob): WorkspaceMode {
  if (hasStarted(job.investorGuidelineReviewStatus)) return 'analyze';
  if (hasStarted(job.documentVerificationStatus) || hasStarted(job.documentProcessingStatus)) {
    return 'extract';
  }
  return 'intake';
}

function ddBaRowMode(job: RowModeJob): WorkspaceMode {
  switch (normalize(job.status)) {
    case 'document_processing':
      // Intake until documents have been submitted for extraction.
      return hasStarted(job.documentProcessingStatus) ? 'extract' : 'intake';
    case 'document_verification':
      return 'extract';
    case 'investor_guideline_review':
    case 'underwriting_guideline_review':
    case 'completed':
      return 'analyze';
    default:
      // FAILED, ARCHIVED or an unknown status: use the furthest step reached.
      return ddBaModeFromSteps(job);
  }
}

function trRowMode(job: RowModeJob): WorkspaceMode {
  if (!isTitleReviewFinalized(job)) return 'review';
  switch (normalize(job.titleAnalysisStatus)) {
    case 'in_progress':
    case 'completed':
    case 'failed':
      return 'analysis';
    default:
      // pending, blocked, absent or unknown: no analysis run yet.
      return 'findings';
  }
}

/**
 * Row → Mode: the Mode a Workspace opens in for an existing item.
 *
 * Total: always returns a Mode in `WORKSPACE_MODES[feature]`.
 * - DD/BA: intake (no documents submitted) · extract (extraction or field
 *   review) · analyze (analysis, guideline review, ready to complete).
 * - TR: review (processing, or not finalized) · findings (finalized, no
 *   analysis run) · analysis (running, completed or failed).
 */
export function getRowMode(job: RowModeJob | null | undefined): WorkspaceMode {
  if (!job) return 'intake';
  return featureOf(job) === JobType.TR ? trRowMode(job) : ddBaRowMode(job);
}

/**
 * The Mode to open when the URL has no valid `mode`: `intake` for a draft,
 * otherwise the Row → Mode default.
 */
export function getDefaultMode(
  job: RowModeJob | null | undefined,
  options: ModeRuleOptions = {}
): WorkspaceMode {
  if (options.isDraft || !job) return 'intake';
  return getRowMode(job);
}

/**
 * Locked Modes for a Workspace, each with the reason for the `title`.
 *
 * - Draft item (`isDraft`, or no job yet): every Mode except `intake` is
 *   locked (Requirement 3.5).
 * - TR, existing search, review not finalized: `findings` and `analysis` are
 *   locked (Review_Gate, Requirement 8.10).
 * - Otherwise nothing is locked.
 */
export function getLockedModes(
  feature: JobType,
  job: RowModeJob | null | undefined,
  options: ModeRuleOptions = {}
): LockedModes {
  const locked: LockedModes = {};

  if (options.isDraft || !job) {
    const reason = `Create the ${UNIT_OF_WORK[feature]} to unlock`;
    for (const mode of WORKSPACE_MODES[feature]) {
      if (mode !== 'intake') locked[mode] = reason;
    }
    return locked;
  }

  if (feature === JobType.TR && !isTitleReviewFinalized(job)) {
    locked.findings = REVIEW_GATE_REASON;
    locked.analysis = REVIEW_GATE_REASON;
  }
  return locked;
}

/**
 * Read Workspace URL state for a job. A missing or invalid `mode`, or one that
 * is currently locked, falls back to the default Mode (draft → intake,
 * otherwise Row → Mode). Selection params are passed through unchanged.
 */
export function readJobWorkspaceParams(
  search: URLSearchParams | string | null | undefined,
  feature: JobType,
  job: RowModeJob | null | undefined,
  options: ModeRuleOptions = {}
): WorkspaceParams {
  const defaultMode = getDefaultMode(job, options);
  const params = readWorkspaceParams(search, feature, defaultMode);
  if (Object.prototype.hasOwnProperty.call(getLockedModes(feature, job, options), params.mode)) {
    return { ...params, mode: defaultMode };
  }
  return params;
}
